import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalDoc = {
  title: string;
  sections: {
    heading: string;
    body: string;
  }[];
};
const PRIVACY: LegalDoc = {
  title: 'Privacy Policy',
  sections: [{
    heading: 'Introduction',
    body: 'Jose\'s Junk Removal ("we," "us," or "our") respects your privacy. This Privacy Policy explains what information we collect from visitors and customers, how we use it, and the choices you have. By using our website or services, you agree to the practices described below.'
  }, {
    heading: 'Information We Collect',
    body: 'We collect information you voluntarily provide when you call, text, or fill out a form — such as your name, phone number, address, email, and a description of the job. We may also collect basic technical data (IP address, browser type, pages visited) automatically through standard web analytics.'
  }, {
    heading: 'How We Use Your Information',
    body: 'We use your information to respond to inquiries, provide quotes, schedule and perform services, send service-related updates, and improve our website. We do not sell your personal information to third parties.'
  }, {
    heading: 'Communications',
    body: 'By providing your phone number, you consent to receive calls and text messages from us related to your inquiry or service. Standard message and data rates may apply. You can opt out at any time by replying STOP to any text or by asking us directly.'
  }, {
    heading: 'Third-Party Services',
    body: 'We may use third-party tools (such as Google Maps, hosting providers, or analytics services) that process limited data to help operate our website. Each third-party service is governed by its own privacy policy.'
  }, {
    heading: 'Data Security',
    body: 'We take reasonable steps to protect the information we collect. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.'
  }, {
    heading: 'Your Rights',
    body: 'You may request access to, correction of, or deletion of personal information we hold about you by contacting us at (831) 240-7401. We will respond to verified requests within a reasonable timeframe.'
  }, {
    heading: 'Changes to This Policy',
    body: 'We may update this Privacy Policy from time to time. Continued use of our services after changes are posted means you accept the updated policy.'
  }, {
    heading: 'Contact',
    body: "If you have questions about this Privacy Policy, contact Jose's Junk Removal at (831) 240-7401 or via our website. We are based in Monterey, CA."
  }]
};
const TERMS: LegalDoc = {
  title: 'Terms of Service',
  sections: [{
    heading: 'Agreement',
    body: "These Terms of Service govern your use of Jose's Junk Removal's website and services. By contacting us, requesting an estimate, or hiring us, you agree to these terms."
  }, {
    heading: 'Services Provided',
    body: 'We offer junk removal, hauling, light demolition, property cleanouts, and related services in Monterey, CA and surrounding areas (within approximately 50 miles). Specific scope, pricing, and timing are confirmed at the time of estimate.'
  }, {
    heading: 'Estimates & Pricing',
    body: 'All estimates are free and based on the information you provide. Final pricing may be adjusted on-site if the volume, type of material, or scope of work differs from what was originally described. We will always communicate any change before proceeding.'
  }, {
    heading: 'Customer Responsibilities',
    body: 'You agree to provide accurate information about the items and conditions at the job site, to grant safe access to the work area, and to confirm that you have the legal right to dispose of the items being removed.'
  }, {
    heading: 'Items We Do Not Accept',
    body: 'We do not haul hazardous waste (chemicals, fuels, asbestos, biohazards, or certain restricted materials). If such items are discovered on-site, we will decline to remove them and refer you to the appropriate disposal service.'
  }, {
    heading: 'Damages & Liability',
    body: 'Our team takes reasonable care while performing services. We are not liable for pre-existing damage or for damage caused by structural conditions outside our control. Any concerns must be reported within 48 hours of service completion.'
  }, {
    heading: 'Payment',
    body: 'Payment is due upon completion of the job unless otherwise agreed in writing. We accept cash and standard electronic payment methods. Returned or failed payments may incur an additional fee.'
  }, {
    heading: 'Cancellations',
    body: 'You may cancel or reschedule a confirmed appointment by calling (831) 240-7401 as early as possible. Same-day cancellations after a crew has been dispatched may incur a minimum trip fee.'
  }, {
    heading: 'Guarantee',
    body: "If you're not satisfied with our work, contact us and we'll make it right. Our goal is to leave every space better than we found it."
  }, {
    heading: 'Governing Law',
    body: 'These terms are governed by the laws of the State of California. Any dispute will be handled in the courts located in Monterey County, CA.'
  }, {
    heading: 'Contact',
    body: "For any questions about these Terms, contact Jose's Junk Removal at (831) 240-7401."
  }]
};
function LegalModal({
  doc,
  onClose
}: {
  doc: LegalDoc | null;
  onClose: () => void;
}) {
  // Lock body scroll while open
  useEffect(() => {
    if (doc) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [doc]);
  // Close on ESC
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-180" data-mp="2q1hwjc1m2woc">
      {doc && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[2000] flex items-center justify-center p-4 sm:p-6" aria-modal="true" role="dialog" aria-labelledby="legal-modal-title" data-id="element-181" data-mp="2q2jgy74ioviq">
          {/* Backdrop */}
          <div onClick={onClose} className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-182" data-mp="2bd3s4wojr05o" />

          {/* Panel */}
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 28
      }} className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-183" data-mp="3mq6bfzi163tj">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-[#1A1A1A] text-white" data-id="element-184" data-mp="nc8a7b6wyvjp">
              <h2 id="legal-modal-title" className="font-oswald text-2xl font-bold tracking-wide uppercase" data-id="element-185" data-mp="je1214qin2k4">
                {doc.title}
              </h2>
              <button onClick={onClose} aria-label="Close" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors" data-id="element-186" data-mp="2dnuxzy51dn32">
                <X size={22} data-id="element-187" data-mp="1mld2su93wqm8" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-6 py-6 sm:px-8 sm:py-8 flex-1" data-id="element-188" data-mp="17wh1dg1k2bul">
              <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-6" data-id="element-189" data-mp="1cv201b15sngx">
                Last Updated:{' '}
                {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
              <div className="space-y-6" data-id="element-190" data-mp="oxwbtsfbbej2">
                {doc.sections.map((s, i) => <div key={i} data-id="element-191" data-mp="tvo3clc1oymq" data-mpi={i}>
                    <h3 className="font-bold text-[#1A1A1A] text-base mb-2 uppercase tracking-wide" data-id="element-192" data-mp="twz92qp1wggh">
                      {s.heading}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-[15px]" data-id="element-193" data-mp="2oaqo65q5fsjb">
                      {s.body}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-194" data-mp="7p3kqwutxs0q">
              <button onClick={onClose} className="bg-[#C8102E] text-white px-6 py-2.5 rounded font-bold text-sm uppercase tracking-wide hover:bg-red-800 transition-colors" data-id="element-195" data-mp="mdgf5pe9r2t4">
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [openDoc, setOpenDoc] = useState<LegalDoc | null>(null);
  return <>
      <footer className="bg-[#1A1A1A] text-gray-400 py-12 border-t border-gray-800" data-id="element-196" data-mp="10bwo7mzlt5ef">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-197" data-mp="372maih9obs5o">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-198" data-mp="3735rnvri2cin">
            <div className="flex items-center gap-4" data-id="element-199" data-mp="325niq5dhfxan">
              <img src="https://cdn.magicpatterns.com/uploads/hJguF2FSF4VF7Gq2iQCUa2/Jose_Junk_Removal_Logo_(1).png" alt="Jose's Junk Removal Logo" className="h-16 w-auto grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all" data-id="element-200" data-mp="iuruvk66h0z9" />
              <div data-id="element-201" data-mp="3xrci79vmidg">
                <h3 className="font-oswald text-xl font-bold text-white tracking-wide" data-id="element-202" data-mp="3y9dtibebms8">
                  JOSE'S JUNK REMOVAL
                </h3>
                <p className="text-sm" data-id="element-203" data-mp="wln44xkl9k3d">
                  Monterey's #1 Full-Service Cleanout Team
                </p>
              </div>
            </div>

            <div className="text-center md:text-right" data-id="element-204" data-mp="rtc4zwxoa2bv">
              <a href="tel:8312407401" className="text-2xl font-bold text-[#C8102E] hover:text-red-400 transition-colors block mb-2" data-id="element-205" data-mp="mwdd7l1he7gu">
                (831) 240-7401
              </a>
              <p className="text-sm" data-id="element-206" data-mp="1fjpnowudaots">
                Serving Monterey, CA and all areas within 50 miles.
              </p>
              <p className="text-sm mt-1" data-id="element-207" data-mp="1nuydwkkwgh21">
                Available for residential, commercial, construction, and
                specialty jobs.
              </p>

              {/* Social Links */}
              <div className="flex items-center gap-3 justify-center md:justify-end mt-5" data-id="element-208" data-mp="1wsik87xmd3ji">
                <span className="text-xs uppercase tracking-widest font-bold text-gray-500" data-id="element-209" data-mp="21u7u3k93vb60">
                  Follow Us:
                </span>
                <a href="https://www.tiktok.com/@josesjunkremoval5" target="_blank" rel="noopener noreferrer" aria-label="Follow Jose's Junk Removal on TikTok" className="group flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-[#C8102E] hover:border-[#C8102E] transition-all hover:-translate-y-0.5" data-id="element-210" data-mp="2atcpihpdeody">
                  {/* TikTok icon (custom SVG — Lucide doesn't include it) */}
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="text-gray-300 group-hover:text-white transition-colors" aria-hidden="true" data-id="element-211" data-mp="24u82tjh8c1wt">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.6 20.1a6.34 6.34 0 0 0 10.86-4.43V8.92a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.64-.35Z" data-id="element-212" data-mp="29qyd1q6cs3ab" />
                  </svg>
                </a>
                <a href="https://www.facebook.com/profile.php?id=100093628803481" target="_blank" rel="noopener noreferrer" aria-label="Follow Jose's Junk Removal on Facebook" className="group flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-[#C8102E] hover:border-[#C8102E] transition-all hover:-translate-y-0.5" data-id="element-213" data-mp="2sjafse0x56kz">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="text-gray-300 group-hover:text-white transition-colors" aria-hidden="true" data-id="element-214" data-mp="3etith63897nt">
                    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.76 8.43-4.92 8.43-9.94Z" data-id="element-215" data-mp="2r47zc7gdubin" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/josesjunk" target="_blank" rel="noopener noreferrer" aria-label="Follow Jose's Junk Removal on Instagram" className="group flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-[#C8102E] hover:border-[#C8102E] transition-all hover:-translate-y-0.5" data-id="element-216" data-mp="fckkq7rl9ec6">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300 group-hover:text-white transition-colors" aria-hidden="true" data-id="element-217" data-mp="27cawwyyl3i9t">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" data-id="element-218" data-mp="1jp2quetc056q" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" data-id="element-219" data-mp="1eox3vby0b6uz" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" data-id="element-220" data-mp="1er15tocchvsm" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-800 text-sm flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-221" data-mp="3lcnsnj16juap">
            <p className="text-center md:text-left" data-id="element-222" data-mp="3qed2ivco21x7">
              © {new Date().getFullYear()} Jose's Junk Removal. All rights
              reserved.
            </p>

            <div className="flex items-center gap-5 order-first md:order-none" data-id="element-223" data-mp="37apdng0m4ti">
              <button onClick={() => setOpenDoc(PRIVACY)} className="text-gray-400 hover:text-[#C8102E] transition-colors uppercase tracking-wider text-xs font-semibold" data-id="element-224" data-mp="32o0kusrmcqtf">
                Privacy Policy
              </button>
              <span className="text-gray-700" data-id="element-225" data-mp="3ge455e6xuag8">•</span>
              <button onClick={() => setOpenDoc(TERMS)} className="text-gray-400 hover:text-[#C8102E] transition-colors uppercase tracking-wider text-xs font-semibold" data-id="element-226" data-mp="3gfniox17m7eb">
                Terms of Service
              </button>
            </div>

            <p className="text-center md:text-right" data-id="element-227" data-mp="3r7ea1ie2iwkt">
              One call. One team. Everything gone.
            </p>
          </div>
        </div>
      </footer>

      <LegalModal doc={openDoc} onClose={() => setOpenDoc(null)} data-id="element-228" data-mp="4tw91wtl0fz8" />
    </>;
}